import { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'

import { i18nDateToString } from 'modules/core/lib/dates'

import { useBulkApprovePeriod } from 'modules/attendance/components/TimeTrackingCollection/hooks/useBulkApprovePeriod'

interface UseEmployeeApprovalParams {
  employee: AttendanceCustomPeriodEmployeeItemFragment
  cycle: string
  dateFrom: Date
  dateTo: Date
  onEmployeeUpdate?: (updatedEmployee: AttendanceCustomPeriodEmployeeItemFragment) => void
}

export const useEmployeeApproval = ({
  employee,
  cycle,
  dateFrom,
  dateTo,
  onEmployeeUpdate,
}: UseEmployeeApprovalParams) => {
  const { bulkApprovePeriod, bulkDisapprovePeriod } = useBulkApprovePeriod({
    cycle,
    startOn: i18nDateToString(dateFrom, 'backend'),
    endOn: i18nDateToString(dateTo, 'backend'),
  })

  const findUpdatedEmployee = (
    results: Awaited<ReturnType<typeof bulkApprovePeriod>>
  ): AttendanceCustomPeriodEmployeeItemFragment | undefined => {
    if (!results) return undefined

    for (const result of results) {
      const nodes = result?.data?.attendance?.customPeriodEmployeesConnection?.nodes
      if (!nodes) continue

      const updated = nodes.find(
        (node: AttendanceCustomPeriodEmployeeItemFragment) => node.id === employee.id
      )

      if (updated) return updated
    }

    return undefined
  }

  const handleApprove = async () => {
    const refetchResult = await bulkApprovePeriod([employee.id?.toString() ?? ''])
    if (!refetchResult) {
      return false
    }

    const updatedEmployee = findUpdatedEmployee(refetchResult) ?? {
      ...employee,
      reviewStatus: 'approved' as const,
    }

    onEmployeeUpdate?.(updatedEmployee)

    return true
  }

  const handleDisapprove = async () => {
    const refetchResult = await bulkDisapprovePeriod([employee.id?.toString() ?? ''])
    if (!refetchResult) return false

    const updatedEmployee = findUpdatedEmployee(refetchResult) ?? {
      ...employee,
      reviewStatus: 'pending' as const,
    }

    onEmployeeUpdate?.(updatedEmployee)

    return true
  }

  return {
    handleApprove,
    handleDisapprove,
  }
}
